import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { Receipt } from '@deposits/shared';
import { Money } from '../../components/Money';
import { summarize } from './SummaryBar';

interface SelectionBarProps {
  selectedReceipts: Receipt[];
  onCreateDeposit: () => void;
  onClear: () => void;
}

/**
 * Floating bulk-action bar — same pattern as the Arbox Expenses BulkActions.
 * Desktop: rounded pill centered at the bottom, 46px tall, white bg + popup
 * shadow, vertical dividers between sections. Mobile: full-width, no rounded
 * corners, optional wrap.
 *
 * Portaled to document.body so the iOS Safari "fixed inside overflow:auto"
 * bug can't scroll/hide the bar with the page container.
 */
export function SelectionBar({
  selectedReceipts,
  onCreateDeposit,
  onClear,
}: SelectionBarProps) {
  if (selectedReceipts.length === 0) return null;
  const t = summarize(selectedReceipts);

  const divider = (
    <div
      className="w-px bg-main-text shrink-0 hidden sm:block"
      style={{ height: 25 }}
    />
  );

  const stat = (label: string, value: React.ReactNode) => (
    <div
      className="flex items-center gap-2 whitespace-nowrap"
      style={{ fontFamily: 'Ploni, sans-serif' }}
    >
      <span className="text-secondary-text">{label}</span>
      <span className="text-main-text font-semibold">{value}</span>
    </div>
  );

  return createPortal(
    <div
      dir="rtl"
      className="fixed bottom-0 left-0 right-0 sm:bottom-6 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 z-50 sm:!w-[60%] sm:min-w-[640px]"
    >
      <div
        className="bg-white sm:rounded shadow-popup flex items-center sm:justify-between px-2 sm:px-4 py-2 sm:py-0 sm:h-[46px] gap-x-3 gap-y-2 flex-wrap sm:flex-nowrap sm:overflow-x-auto"
        style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.16)' }}
      >
        <span
          className="hidden sm:inline text-main-text whitespace-nowrap"
          style={{ fontFamily: 'Ploni, sans-serif', fontWeight: 600 }}
        >
          {selectedReceipts.length} נבחרו
        </span>
        {divider}
        {stat("צ'קים", <span dir="ltr">{t.checkCount}</span>)}
        {divider}
        {stat("סה\"כ צ'קים", <Money amount={t.checksTotal} />)}
        {divider}
        {stat('סה"כ מזומן', <Money amount={t.cashTotal} />)}
        {divider}
        <button
          onClick={onCreateDeposit}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity whitespace-nowrap py-1"
          style={{
            color: '#1936f7',
            fontFamily: 'Ploni, sans-serif',
            fontWeight: 600,
          }}
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: 14 }} />
          <span>צור הפקדה חדשה</span>
        </button>
        <button
          onClick={onClear}
          className="text-secondary-text hover:text-main-text transition-colors shrink-0 ms-auto sm:ms-0"
          aria-label="סגור"
        >
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 14 }} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
